/**
 * Thin wrapper around `@theg1group/rbac-ui`'s AuthProvider/useAuth that
 * binds them to the admin app's HTTP client. Public surface is
 * intentionally identical to the previous in-tree implementation —
 * existing call sites (`useAuth().user.permissions`, etc.) keep working.
 */

import {
	AuthProvider as RbacAuthProvider,
	type AuthUser as RbacAuthUser,
	useAuth as useRbacAuth,
} from "@theg1group/rbac-ui";
import { type ReactNode, createElement } from "react";
import { ApiError, logout as apiLogout, sharedApi } from "./api";

export type AuthUser = RbacAuthUser & {
	sitePermissions: Record<string, string[]>;
};

export const useAuth = useRbacAuth;

async function fetchUser(): Promise<AuthUser | null> {
	try {
		const data = await sharedApi.get<AuthUser>("/users/me");
		return {
			...data,
			permissions: data.permissions ?? [],
			appAccess: data.appAccess ?? [],
			sitePermissions: data.sitePermissions ?? {},
		};
	} catch (err) {
		if (err instanceof ApiError && err.status === 401) return null;
		throw err;
	}
}

export function AuthProvider({ children }: { children: ReactNode }) {
	return createElement(RbacAuthProvider, {
		fetchUser,
		onLogout: apiLogout,
		children,
	});
}
